import { useEffect } from 'react'
import type { RefObject } from 'react'
import { useLocation, useNavigationType } from 'react-router'

const POSITIONS_STORAGE_KEY = 'tactrl.scroll-positions'
const ENTRIES_STORAGE_KEY = 'tactrl.scroll-history-entries'
const MAX_ENTRIES = 50
const RESTORE_TIMEOUT_MS = 2000
const RESTORE_REQUEST_TTL_MS = 2000

const readStoredMap = <T>(storageKey: string): Map<string, T> => {
  try {
    const stored = sessionStorage.getItem(storageKey)
    if (!stored) return new Map()
    return new Map(Object.entries(JSON.parse(stored) as Record<string, T>))
  } catch {
    return new Map()
  }
}

/** Scroll position per history entry. */
const positions = readStoredMap<number>(POSITIONS_STORAGE_KEY)
/** The history entry a url was last seen in, for navigations that make a new one. */
const historyEntries = readStoredMap<string>(ENTRIES_STORAGE_KEY)

let persistFrame = 0
const persist = () => {
  if (persistFrame) return
  persistFrame = requestAnimationFrame(() => {
    persistFrame = 0
    try {
      sessionStorage.setItem(
        POSITIONS_STORAGE_KEY,
        JSON.stringify(Object.fromEntries(positions))
      )
      sessionStorage.setItem(
        ENTRIES_STORAGE_KEY,
        JSON.stringify(Object.fromEntries(historyEntries))
      )
    } catch {
      // sessionStorage can be unavailable (private mode, quota) - positions
      // then only live for as long as the tab does, which is acceptable.
    }
  })
}

const setCapped = <T>(map: Map<string, T>, key: string, value: T) => {
  map.delete(key)
  map.set(key, value)
  while (map.size > MAX_ENTRIES) {
    const oldest = map.keys().next()
    if (oldest.done) break
    map.delete(oldest.value)
  }
}

const savePosition = (key: string, url: string, position: number) => {
  setCapped(positions, key, position)
  setCapped(historyEntries, url, key)
  persist()
}

// Breadcrumb links keep the trailing slash the crumb was built with, so urls
// have to be compared in a single shape.
const normalizeUrl = (pathname: string, search = '') =>
  `${pathname.replace(/\/+$/, '') || '/'}${search}`

let restoreRequestedAt = 0

/**
 * Location state for a navigation that changes the page in place - a filter or
 * a selection written to the url - and should leave the scroll where it is.
 */
export const KEEP_SCROLL_STATE = { keepScroll: true } as const

const keepsScroll = (state: unknown) =>
  typeof state === 'object' &&
  state !== null &&
  (state as { keepScroll?: unknown }).keepScroll === true

/**
 * Marks the navigation that follows as one that should land where the user last
 * left the page it goes to, rather than at the top. Navigations that make a new
 * history entry - a breadcrumb click, say - have no position of their own.
 */
export const requestScrollRestore = () => {
  restoreRequestedAt = Date.now()
}

const takeRequestedPosition = (url: string) => {
  const requestedAt = restoreRequestedAt
  restoreRequestedAt = 0

  if (Date.now() - requestedAt > RESTORE_REQUEST_TTL_MS) return 0

  // A url the page was never left at - a differently filtered list, say -
  // has nothing to go back to.
  const key = historyEntries.get(url)
  if (!key) return 0

  return positions.get(key) ?? 0
}

// The main content element only scrolls above the largePad breakpoint; below it
// the styling hands scrolling back to the document.
const resolveScroller = (content: HTMLElement | null): Element | null => {
  if (content && getComputedStyle(content).overflowY !== 'visible')
    return content
  return document.scrollingElement
}

/**
 * Remembers the main scroll position per history entry and puts it back when the
 * user navigates back or forward, or follows a link that asked for it through
 * `requestScrollRestore`. A navigation carrying `KEEP_SCROLL_STATE` stays where
 * it is. Any other navigation starts at the top.
 *
 * Restoring is retried until the page has grown tall enough to hold the saved
 * offset, since pages render before their data has loaded.
 */
export const useScrollRestoration = (
  contentRef: RefObject<HTMLElement | null>
) => {
  const { key, pathname, search, state } = useLocation()
  const keepScroll = keepsScroll(state)
  const navigationType = useNavigationType()

  useEffect(() => {
    const scroller = resolveScroller(contentRef.current)
    if (!scroller) return

    const url = normalizeUrl(pathname, search)
    const scrollTarget =
      scroller === document.scrollingElement ? window : scroller
    const target =
      navigationType === 'POP'
        ? positions.get(key) ?? 0
        : takeRequestedPosition(url)

    let restoring = target > 0
    let restoreFrame = 0
    const deadline = performance.now() + RESTORE_TIMEOUT_MS

    const stopRestoring = () => {
      restoring = false
      cancelAnimationFrame(restoreFrame)
    }

    const restoreStep = () => {
      if (!restoring) return
      const maxScroll = scroller.scrollHeight - scroller.clientHeight
      scroller.scrollTop = Math.min(target, maxScroll)
      if (maxScroll >= target || performance.now() > deadline) {
        stopRestoring()
        return
      }
      restoreFrame = requestAnimationFrame(restoreStep)
    }

    if (restoring) {
      restoreStep()
    } else if (keepScroll) {
      savePosition(key, url, scroller.scrollTop)
    } else {
      scroller.scrollTop = 0
    }

    const onScroll = () => {
      if (restoring) return
      savePosition(key, url, scroller.scrollTop)
    }

    // Listening for input rather than for scroll events, so that our own
    // programmatic scrolling doesn't count as the user taking over.
    const takeoverEvents = ['wheel', 'touchstart', 'keydown'] as const

    scrollTarget.addEventListener('scroll', onScroll, { passive: true })
    takeoverEvents.forEach(event =>
      window.addEventListener(event, stopRestoring, { passive: true })
    )

    return () => {
      stopRestoring()
      scrollTarget.removeEventListener('scroll', onScroll)
      takeoverEvents.forEach(event =>
        window.removeEventListener(event, stopRestoring)
      )
    }
  }, [contentRef, keepScroll, key, navigationType, pathname, search])
}
